import { Component, Input, OnDestroy, OnInit, TemplateRef } from '@angular/core';
import { RbCssTimer } from '@inst-iot/bosch-angular-ui-components/utils/css-timer/css-timer';
import { takeUntil, debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { Subject, forkJoin } from 'rxjs';
import { VehicleInfoService } from 'src/app/_services/vehicle-info.service';
import { Vehicle } from 'src/app/_shared/models/vehicle';
import { LoadingEntity, ModalService, PageChange, PageData, ToasterService } from '@inst-iot/bosch-angular-ui-components';
import { NavigationService } from 'src/app/_services/navigation.service';
import { NotificationToastComponent } from 'src/app/_shared/components/utils/notification-toast/notification-toast.component';
import { environment } from 'src/environments/environment';
import { sortRbTableData } from 'src/app/_shared/utils/sort-utils';

@Component({
  selector: 'app-vehicle-onboarding',
  templateUrl: './vehicle-onboarding.component.html',
  styleUrls: ['./vehicle-onboarding.component.scss'],
  providers: [
    {
      provide: ToasterService,
      useFactory: () => new ToasterService(NotificationToastComponent)
    }
  ]
})
export class VehicleOnboardingComponent implements OnInit, OnDestroy {
  private readonly unsubscribe$ = new Subject<void>();
  @Input() fleetId: string[] = [];
  rowItemChange$: Subject<RbCssTimer> = new Subject();
  repositoryLoader = new LoadingEntity<any[]>();
  notSupportedVehicles: Vehicle[] = [];
  notIngestedVehicles: Vehicle[] = [];
  supportedVehicles: any[] = [];
  liveVehicleData: any[] = [];
  maxYear: any = new Date().getFullYear() + 1;
  editableVehicleInfo: any;
  oldVehicleData: any;

  reason: string = '';
  customReasonSelectOptions: string[] = ['Incorrect vehicle configuration', 'Dongle moved to new vehicle', 'Other reason'];
  additionalReasonComment: string = '';
  pendingRequests: any[] = [];

  // Sorting table data
  readonly sortData = sortRbTableData;

  // Pagination data
  limit = 21;
  lastNavigate: PageChange | undefined;
  pageData = new PageData({ number: 0, size: this.limit, totalElements: 0, numberOfElements: 21 });
  // Separate pagination controls for ingested tab
  limitIngested = 21;
  lastNavigateIngested: PageChange | undefined;
  pageDataIngested = new PageData({ number: 0, size: this.limitIngested, totalElements: 0, numberOfElements: 21 });
  // Client-side cache for full dataset (used for global search/sort)
  allNotSupportedVehicles: Vehicle[] = [];
  searchQuery: string = '';
  sortField: string = '';
  sortDirection: '' | 'asc' | 'desc' = '';
  private readonly search$ = new Subject<string>();
  isPrefetchingAll = false;
  // Duplicate for notIngestedVehicles (separate tab, separate state)
  allNotIngestedVehicles: Vehicle[] = [];
  searchQueryIngested: string = '';
  sortFieldIngested: string = '';
  sortDirectionIngested: '' | 'asc' | 'desc' = '';
  private readonly searchIngested$ = new Subject<string>();
  isPrefetchingIngestedAll = false;

  constructor(
    private readonly vehicleInfoService: VehicleInfoService,
    private readonly navigationService: NavigationService,
    private readonly modalService: ModalService,
    private readonly toaster: ToasterService) { }

  ngOnInit(): void {
    this.retrieveNotSupportedVehicle();
    this.retrieveNotIngestedVehicle();
    // start background prefetch of the full dataset (non-blocking)
    this.retrieveAllNotSupportedVehicles();
    // also prefetch full dataset for ingested tab to keep behavior consistent
    this.retrieveAllNotIngestedVehicles();

    this.vehicleInfoService.getSupportedVehicles().pipe(takeUntil(this.unsubscribe$)).subscribe({
      next: (supportedVehicles) => {
        this.supportedVehicles = supportedVehicles
      },
      error: (error) => {
        console.error('Error fetching supported vehicles:', error);
      }
    })

    const credentials = sessionStorage.getItem(`credentials_${environment.appId}`);
    this.checkPendingRequests(credentials);
    
    // search debounce: trigger client-side apply or prefetch when user types (1° tab)
    this.search$.pipe(debounceTime(300), distinctUntilChanged(), takeUntil(this.unsubscribe$)).subscribe(() => {
      this.pageData = new PageData({ ...this.pageData, number: 0 });
      if (this.allNotSupportedVehicles.length === 0) {
        this.retrieveAllNotSupportedVehicles();
      } else {
        this.applyClientSideFilterSort();
      }
    });

    // search debounce: trigger client-side apply or prefetch when user types (2° tab)
    this.searchIngested$.pipe(debounceTime(300), distinctUntilChanged(), takeUntil(this.unsubscribe$)).subscribe(() => {
      this.pageDataIngested = new PageData({ ...this.pageDataIngested, number: 0 });
      if (this.allNotIngestedVehicles.length === 0) {
        this.retrieveAllNotIngestedVehicles();
      } else {
        this.applyClientSideFilterSortIngested();
      }
    });
  }

  /* 1° Tab */
  retrieveNotSupportedVehicle() {
    this.vehicleInfoService.getNotSupportedVehicles(this.pageData.number + 1 , 21, this.fleetId).pipe(takeUntil(this.unsubscribe$)).subscribe({
      next: (response) => {
        this.notSupportedVehicles = response.data;
        this.notSupportedVehicles.forEach(v => this.resetVehicleDefaults(v));
        this.pageData = new PageData({ number: this.pageData.number, size: this.limit, totalElements: response.total, numberOfElements: 21 });
      },
      error: (error) => {
        console.error('Error fetching not supported vehicles:', error);
      }
    })
  }

  // Fetch all pages and store them in `allNotSupportedVehicles` (used for global search/sort)
  retrieveAllNotSupportedVehicles() {
    if (this.isPrefetchingAll) return;
    this.isPrefetchingAll = true;
    this.vehicleInfoService.getNotSupportedVehicles(1, this.limit, this.fleetId).pipe(takeUntil(this.unsubscribe$)).subscribe({
      next: firstResp => {
        const total = firstResp.total || (firstResp.data ? firstResp.data.length : 0);
        this.allNotSupportedVehicles = firstResp.data || [];
        this.allNotSupportedVehicles.forEach(v => this.resetVehicleDefaults(v));
        const pages = Math.ceil(total / this.limit);
        const requests = [];
        for (let p = 2; p <= pages; p++) {
          requests.push(this.vehicleInfoService.getNotSupportedVehicles(p, this.limit, this.fleetId));
        }
        if (requests.length === 0) {
          this.isPrefetchingAll = false;
          this.applyClientSideFilterSort();
          return;
        }
        forkJoin(requests).pipe(takeUntil(this.unsubscribe$)).subscribe(resArray => {
          const rest = resArray.flatMap((r: any) => r.data || []);
          this.allNotSupportedVehicles = (firstResp.data || []).concat(rest);
          this.allNotSupportedVehicles.forEach(v => this.resetVehicleDefaults(v));
          this.isPrefetchingAll = false;
          this.applyClientSideFilterSort();
        }, err => {
          console.error('Error fetching additional pages for prefetch', err);
          this.isPrefetchingAll = false;
          // fallback: still apply on what we have
          this.applyClientSideFilterSort();
        });
      },
      error: err => {
        console.error('Error fetching first page for prefetch', err);
        this.isPrefetchingAll = false;
      }
    });
  }

  applyClientSideFilterSort() {
    const q = (this.searchQuery || '').trim().toLowerCase();
    let list = [...this.allNotSupportedVehicles];
    if (q) {
      list = list.filter(v =>
        (v.name || '').toString().toLowerCase().includes(q) ||
        (v.batteryIdentifier || '').toString().toLowerCase().includes(q)  ||
        (v.enrolled || '').toString().toLowerCase().includes(q) /* ||
        (v.vehicleModel || '').toString().toLowerCase().includes(q) ||
        (v.productionYear ? String(v.productionYear) : '').toLowerCase().includes(q) */
      );
    }
    if (this.sortField) {
      list.sort((a: any, b: any) => {
        const av = (a[this.sortField] ?? '').toString().toLowerCase();
        const bv = (b[this.sortField] ?? '').toString().toLowerCase();
        let res = 0;
        if (av < bv) {
          res = -1;
        } else if (av > bv) {
          res = 1;
        }
        return this.sortDirection === 'desc' ? -res : res;
      });
    }
    const start = this.pageData.number * this.limit;
    this.notSupportedVehicles = list.slice(start, start + this.limit);
    this.pageData = new PageData({ ...this.pageData, totalElements: list.length, numberOfElements: this.notSupportedVehicles.length });
  }

  private resetVehicleDefaults(vehicle: any) {
    vehicle.vehicleBrand = '';
    vehicle.vehicleModel = '';
    vehicle.nominalUsableEnergy = '';
    vehicle.netEnergyWh = 0;
    vehicle.grossEnergyWh = 0;
    vehicle.modelYear = '';
    vehicle.productionYear = '';
    vehicle.grossCapacityAh = 0;
    vehicle.netCapacityAh = 0;
    vehicle.batteryType = '';
  }

  private getScopedSupportedVehicles(vehicleContext: any): any[] {
    const isCustomVehicleDevice = vehicleContext?.deviceType === 'CustomVehicleDevice';
    return this.supportedVehicles.filter((supportedVehicle: any) => {
      if (isCustomVehicleDevice) {
        return supportedVehicle.geotabOemSupported === true;
      }
      return supportedVehicle.geotabSupported === true;
    });
  }

  private getScopedSupportedVehiclesForIndex(i: number, fallbackVehicleContext?: any): any[] {
    if (this.liveVehicleData[i]?.deviceType) {
      return this.getScopedSupportedVehicles(this.liveVehicleData[i]);
    }
    if (fallbackVehicleContext?.deviceType) {
      return this.getScopedSupportedVehicles(fallbackVehicleContext);
    }
    const sourceVehicle = this.notSupportedVehicles.find((vehicle: any) => vehicle._id === this.liveVehicleData[i]?.vehicleId);
    return this.getScopedSupportedVehicles(sourceVehicle || {});
  }

  getBrandOptionsForVehicle(vehicleContext: any): string[] {
    return this.getScopedSupportedVehicles(vehicleContext)
      .map((vehicle: any) => vehicle.vehicleBrand)
      .filter((brand, index, self) => brand && self.indexOf(brand) === index)
      .sort((a: string, b: string) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
  }

  onSearchChange(value: string) {
    this.searchQuery = value;
    this.search$.next(value);
  }

  onSortChange(event: { id: string, direction: string }) {
    this.sortField = event.id || '';
    this.sortDirection = (event.direction as '' | 'asc' | 'desc') || '';
    this.pageData = new PageData({ ...this.pageData, number: 0 });
    if (this.allNotSupportedVehicles.length === 0) {
      // need full dataset to sort globally
      this.retrieveAllNotSupportedVehicles();
    } else {
      this.applyClientSideFilterSort();
    }
  }
  /* --- End 1° Tab --- */

  /* --- 2° Tab --- */
  retrieveNotIngestedVehicle() {
    this.vehicleInfoService.getNotIngestedVehicles(this.pageDataIngested.number + 1, this.limitIngested, this.fleetId).pipe(takeUntil(this.unsubscribe$)).subscribe({
      next: (response) => {
        this.notIngestedVehicles = response.data || [];
        // keep existing configured values for ingested vehicles; do not overwrite vehicleBrand/model
        this.pageDataIngested = new PageData({ number: this.pageDataIngested.number, size: this.limitIngested, totalElements: response.total || (response.data ? response.data.length : 0), numberOfElements: (response.data || []).length });
      },
      error: (error) => {
        console.error('Error fetching not ingested vehicles:', error);
      }
    });
  }

  // Fetch all pages for notIngestedVehicles and cache client-side (used for global search/sort on that tab)
  retrieveAllNotIngestedVehicles() {
    if (this.isPrefetchingIngestedAll) return;
    this.isPrefetchingIngestedAll = true;
    this.vehicleInfoService.getNotIngestedVehicles(1, this.limitIngested, this.fleetId).pipe(takeUntil(this.unsubscribe$)).subscribe({
      next: firstResp => {
        const total = firstResp.total || (firstResp.data ? firstResp.data.length : 0);
        this.allNotIngestedVehicles = firstResp.data || [];
        const pages = Math.ceil(total / this.limitIngested);
        const requests = [];
        for (let p = 2; p <= pages; p++) {
          requests.push(this.vehicleInfoService.getNotIngestedVehicles(p, this.limitIngested, this.fleetId));
        }
        if (requests.length === 0) {
          this.isPrefetchingIngestedAll = false;
          this.applyClientSideFilterSortIngested();
          return;
        }
        forkJoin(requests).pipe(takeUntil(this.unsubscribe$)).subscribe(resArray => {
          const rest = resArray.flatMap((r: any) => r.data || []);
          this.allNotIngestedVehicles = (firstResp.data || []).concat(rest);
          this.isPrefetchingIngestedAll = false;
          this.applyClientSideFilterSortIngested();
        }, err => {
          console.error('Error fetching additional pages for ingested prefetch', err);
          this.isPrefetchingIngestedAll = false;
          this.applyClientSideFilterSortIngested();
        });
      },
      error: err => {
        console.error('Error fetching first page for ingested prefetch', err);
        this.isPrefetchingIngestedAll = false;
      }
    });
  }

  applyClientSideFilterSortIngested() {
    const q = (this.searchQueryIngested || '').trim().toLowerCase();
    let list = [...this.allNotIngestedVehicles];
    if (q) {
      list = list.filter(v =>
        (v.name || '').toString().toLowerCase().includes(q) ||
        (v.batteryIdentifier || '').toString().toLowerCase().includes(q) ||
        (v.vehicleBrand || '').toString().toLowerCase().includes(q) ||
        (v.vehicleModel || '').toString().toLowerCase().includes(q) ||
        (v.productionYear ? String(v.productionYear) : '').toLowerCase().includes(q) ||
        (v.batteryType || '').toString().toLowerCase().includes(q) ||
        (v.firmwareVersion || '').toString().toLowerCase().includes(q)
      );
    }
    if (this.sortFieldIngested) {
      list.sort((a: any, b: any) => {
        const av = (a[this.sortFieldIngested] ?? '').toString().toLowerCase();
        const bv = (b[this.sortFieldIngested] ?? '').toString().toLowerCase();
        let res = 0;
        if (av < bv) {
          res = -1;
        } else if (av > bv) {
          res = 1;
        }
        return this.sortDirectionIngested === 'desc' ? -res : res;
      });
    }
    // apply pagination slice like notSupportedVehicles (separate pageDataIngested)
    const start = this.pageDataIngested.number * this.limitIngested;
    this.notIngestedVehicles = list.slice(start, start + this.limitIngested);
    this.pageDataIngested = new PageData({ ...this.pageDataIngested, totalElements: list.length, numberOfElements: this.notIngestedVehicles.length });
  }

  onSearchChangeIngested(value: string) {
    this.searchQueryIngested = value;
    this.searchIngested$.next(value);
  }

  onSortChangeIngested(event: { id: string, direction: string }) {
    this.sortFieldIngested = event.id || '';
    this.sortDirectionIngested = (event.direction as '' | 'asc' | 'desc') || '';
    if (this.allNotIngestedVehicles.length === 0) {
      this.retrieveAllNotIngestedVehicles();
    } else {
      this.applyClientSideFilterSortIngested();
    }
  }
  /* End  2° Tab */

  checkNewVehicles() {
    this.vehicleInfoService.storeVehicles({}).pipe(takeUntil(this.unsubscribe$)).subscribe({
      next: (response) => {
        const label = `Battery in the Cloud is currently retrieving and processing MyGeotab assets. The time required to complete this operation depends on the number of assets. In the meantime, you can continue exploring the dashboard.`;
        const icon = `rb-ic-alert-success`;
        this.openToast(label, icon, 'success');
        this.retrieveNotSupportedVehicle();
      },
      error: (error) => {
        console.error('Error storing vehicles:', error);
      },
      complete: () => {
      }
    });
  }

  updateVehicleModelList(i: number, vehicleId: string, value: string, vehicleContext?: any) {
    if(this.liveVehicleData[i] && this.liveVehicleData[i].vehicleBrand !== value) {
      this.notSupportedVehicles.forEach(vehicle => {
        if(vehicle._id === this.liveVehicleData[i].vehicleId) {
          vehicle.vehicleModel = '';
          vehicle.nominalUsableEnergy = '';
          vehicle.netEnergyWh = 0;
          vehicle.grossEnergyWh = 0;
          vehicle.modelYear = '';
          vehicle.productionYear = '';
          vehicle.grossCapacityAh = 0;
          vehicle.netCapacityAh = 0;
          vehicle.batteryType = '';
        }
      })
    }
    
    this.liveVehicleData[i] = { index: i, vehicleId: vehicleId, vehicleBrand: value, deviceType: vehicleContext?.deviceType }
    
    const models = this.getScopedSupportedVehiclesForIndex(i, vehicleContext)
      .filter(vehicle => vehicle.vehicleBrand === this.liveVehicleData[i].vehicleBrand)
      .map(vehicle => vehicle.vehicleModel)
      .filter((model, index, self) => model && self.indexOf(model) === index);
      
    this.liveVehicleData[i].vehicleModelList = models;
  }

  updateBatteryEnergyList(i: number, value: string, vehicleContext?: any) {
    if(this.liveVehicleData[i] && this.liveVehicleData[i].vehicleModel !== value) {
      this.notSupportedVehicles.forEach(vehicle => {
        if(vehicle._id === this.liveVehicleData[i].vehicleId) {
          vehicle.nominalUsableEnergy = '';
          vehicle.netEnergyWh = 0;
          vehicle.grossEnergyWh = 0;
          vehicle.modelYear = '';
          vehicle.productionYear = '';
          vehicle.grossCapacityAh = 0;
          vehicle.netCapacityAh = 0;
          vehicle.batteryType = '';
        }
      })
    }
    
    this.liveVehicleData[i].vehicleModel = value;
    
    this.updateBatteryEnergyOptions(i, vehicleContext);
  }

  private updateBatteryEnergyOptions(i: number, vehicleContext?: any): void {
    const batteryEnergyList = this.getScopedSupportedVehiclesForIndex(i, vehicleContext)
      .filter(vehicle => (
        vehicle.vehicleBrand === this.liveVehicleData[i].vehicleBrand &&
        vehicle.vehicleModel === this.liveVehicleData[i].vehicleModel
      ))
      .map(vehicle => `${vehicle.grossEnergyWh / 1000} kWh / ${vehicle.netEnergyWh / 1000} kWh`)
      .filter((energy, index, self) => energy && self.indexOf(energy) === index);

    this.liveVehicleData[i].grossEnergyWhList = batteryEnergyList;
  }

  updateModelYearList(i: number, value: string, vehicleContext?: any) {
    if(this.liveVehicleData[i] && this.liveVehicleData[i].nominalUsableEnergy !== value) {
      this.notSupportedVehicles.forEach(vehicle => {
        if(vehicle._id === this.liveVehicleData[i].vehicleId) {
          vehicle.modelYear = '';
          vehicle.productionYear = '';
          vehicle.grossCapacityAh = 0;
          vehicle.netCapacityAh = 0;
          vehicle.batteryType = '';
        }
      })
    }
    
    this.updateModelYearOptions(i, value, vehicleContext);
  }

  private updateModelYearOptions(i: number, value: string, vehicleContext?: any): void {
    this.liveVehicleData[i].nominalUsableEnergy = value;

    const [nominalStr, usableStr] = value.split('/');
    const parseEnergy = (str: string): number | null => {
      if (!str) return null;
      const match = str.trim().match(/^([\d.,]+)\s*(kWh|Wh)?$/i);
      if (!match) return null;
      let num = parseFloat(match[1].replace(',', '.'));
      if (isNaN(num)) return null;
      if (match[2]?.toLowerCase() === 'kwh') num *= 1000;
      return num;
    };

    this.liveVehicleData[i].grossEnergyWh = parseEnergy(nominalStr);
    this.liveVehicleData[i].netEnergyWh = parseEnergy(usableStr);
    this.liveVehicleData[i].modelYearList = this.getScopedSupportedVehiclesForIndex(i, vehicleContext)
      .filter(vehicle => (
        vehicle.vehicleBrand === this.liveVehicleData[i].vehicleBrand &&
        vehicle.vehicleModel === this.liveVehicleData[i].vehicleModel &&
        vehicle.grossEnergyWh === this.liveVehicleData[i].grossEnergyWh &&
        vehicle.netEnergyWh === this.liveVehicleData[i].netEnergyWh
      ))
      .map(vehicle => vehicle.modelYear)
      .filter((year, index, self) => year && self.indexOf(year) === index);
  }

  updateProductionYear(i: number, value: string, vehicleContext?: any) {
    this.updateProductionYearValues(i, value, vehicleContext);
  }

  private updateProductionYearValues(i: number, value: string, vehicleContext?: any): void {
    this.liveVehicleData[i].productionYear = value;
    
    const productionYear = parseInt(value, 10);
    const modelYearList = (this.liveVehicleData[i].modelYearList || [])
      .map((y: any) => parseInt(y, 10))
      .filter((y: number) => !isNaN(y) && y <= productionYear);
      
    if (modelYearList.length > 0) {
      const closest = modelYearList.reduce((prev: number, curr: number) =>
        Math.abs(curr - productionYear) < Math.abs(prev - productionYear) ? curr : prev
      );
      this.liveVehicleData[i].modelYear = closest;
    } else {
      this.liveVehicleData[i].modelYear = null;
    }
    
    const selectedModelYear = this.liveVehicleData[i].modelYear;
    const scopedSupportedVehicles = this.getScopedSupportedVehiclesForIndex(i, vehicleContext);

    const grossCapacityAhList = scopedSupportedVehicles
      .filter(vehicle => (
        vehicle.vehicleBrand === this.liveVehicleData[i].vehicleBrand && 
        vehicle.vehicleModel === this.liveVehicleData[i].vehicleModel &&
        vehicle.grossEnergyWh === this.liveVehicleData[i].grossEnergyWh &&
        vehicle.netEnergyWh === this.liveVehicleData[i].netEnergyWh &&
        String(vehicle.modelYear) === String(selectedModelYear ?? '')
      ))
      .map(vehicle => vehicle.grossCapacityAh)
      .filter((capacity, index, self) => capacity && self.indexOf(capacity) === index);

    const netCapacityAhList = scopedSupportedVehicles
      .filter(vehicle => (
        vehicle.vehicleBrand === this.liveVehicleData[i].vehicleBrand && 
        vehicle.vehicleModel === this.liveVehicleData[i].vehicleModel &&
        vehicle.grossEnergyWh === this.liveVehicleData[i].grossEnergyWh &&
        vehicle.netEnergyWh === this.liveVehicleData[i].netEnergyWh &&
        String(vehicle.modelYear) === String(selectedModelYear ?? '')
      ))
      .map(vehicle => vehicle.netCapacityAh)
      .filter((capacity, index, self) => capacity && self.indexOf(capacity) === index);
    
    const batteryTypeList = scopedSupportedVehicles
      .filter(vehicle => (
        vehicle.vehicleBrand === this.liveVehicleData[i].vehicleBrand && 
        vehicle.vehicleModel === this.liveVehicleData[i].vehicleModel &&
        vehicle.grossEnergyWh === this.liveVehicleData[i].grossEnergyWh &&
        vehicle.netEnergyWh === this.liveVehicleData[i].netEnergyWh &&
        String(vehicle.modelYear) === String(selectedModelYear ?? '')
      ))
      .map(vehicle => vehicle.batteryType)
      .filter((type, index, self) => type && self.indexOf(type) === index);
    
    const urlImageList = scopedSupportedVehicles
      .filter(vehicle => (
        vehicle.vehicleBrand === this.liveVehicleData[i].vehicleBrand && 
        vehicle.vehicleModel === this.liveVehicleData[i].vehicleModel &&
        vehicle.grossEnergyWh === this.liveVehicleData[i].grossEnergyWh &&
        vehicle.netEnergyWh === this.liveVehicleData[i].netEnergyWh &&
        String(vehicle.modelYear) === String(selectedModelYear ?? '')
      ))
      .map(vehicle => vehicle.urlImage)
      .filter((url, index, self) => url && self.indexOf(url) === index);
    
    this.liveVehicleData[i].grossCapacityAh = grossCapacityAhList[0];
    this.liveVehicleData[i].netCapacityAh = netCapacityAhList[0];
    this.liveVehicleData[i].batteryType = batteryTypeList[0];
    this.liveVehicleData[i].urlImage = urlImageList[0];
  }

  updateVehicle(i: number) {
    const body = {
      vehicleBrand: this.liveVehicleData[i].vehicleBrand,
      vehicleModel: this.liveVehicleData[i].vehicleModel,
      grossEnergyWh: this.liveVehicleData[i].grossEnergyWh,
      netEnergyWh: this.liveVehicleData[i].netEnergyWh,
      modelYear: this.liveVehicleData[i].modelYear.toString(),
      productionYear: this.liveVehicleData[i].productionYear.toString(),
      grossCapacityAh: this.liveVehicleData[i].grossCapacityAh,
      netCapacityAh: this.liveVehicleData[i].netCapacityAh,
      batteryType: this.liveVehicleData[i].batteryType,
      urlImage: this.liveVehicleData[i].urlImage,
      supported: true,
      onboardedAt: new Date().toISOString()
    }
    this.vehicleInfoService.updateVehicles(this.liveVehicleData[i].vehicleId, body).subscribe({
      next: (response) => {
        const label = `Vehicle updated successfully.`;
        const icon = `rb-ic-alert-success`;
        this.openToast(label, icon, 'success');
        this.retrieveNotSupportedVehicle();
      },
      error: (error) => {
        console.error('Error update vehicle:', error);
      },
      complete: () => {
        this.retrieveNotIngestedVehicle();
      }
    })
  }

  checkOrderNow() {
    this.vehicleInfoService.getOrderNow().pipe(takeUntil(this.unsubscribe$)).subscribe(
      {
        next: (response) => {
          const label = `The number of vehicles subscribed to the Battery in the Cloud service is: ${response.length}. You can subscribe new vehicles from the "Add-In Management". "Add-In Management" can be found in MyGeotab Add-Ins or in the MyAdmin portal.`;
          const icon = `rb-ic-alert-success`;
          this.openToast(label, icon, 'success');
          const body = response
          this.vehicleInfoService.modifyFlag(body).pipe(takeUntil(this.unsubscribe$)).subscribe(
            {
              next: (response) => {
                console.log('Flag updated successfully:', response);
                this.vehicleInfoService.postIngestibleDevices({}).pipe(takeUntil(this.unsubscribe$)).subscribe({
                  next: (response) => {
                    console.log('Ingestible devices posted successfully:', response);
                  },
                  error: (error) => {
                    console.error('Error posting ingestible devices:', error);
                  },
                  complete: () => {
                    this.retrieveNotIngestedVehicle();
                  }
                })
              },
              error: (error) => {
                console.error('Error update flag:', error);
              }
            }
          )
        },
        error: (error) => {
          console.error('Error fetching orderNow:', error);
        },
      }
    )
  }

  goTo(page: string) {
    this.navigationService.navigateTo(page)
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  // EDIT VEHICLE SECTION

  openEditVehicleDialog(tpl: TemplateRef<any>, vehicle: any) {
    this.liveVehicleData = [];
    this.oldVehicleData = JSON.parse(JSON.stringify(vehicle));
    this.editableVehicleInfo = JSON.parse(JSON.stringify(vehicle));
    this.editableVehicleInfo.vehicleBrand = '';
    this.editableVehicleInfo.vehicleModel = '';
    this.editableVehicleInfo.nominalUsableEnergy = '';
    this.editableVehicleInfo.netEnergyWh = 0;
    this.editableVehicleInfo.grossEnergyWh = 0;
    this.editableVehicleInfo.modelYear = '';
    this.editableVehicleInfo.productionYear = '';
    this.editableVehicleInfo.grossCapacityAh = 0;
    this.editableVehicleInfo.netCapacityAh = 0;
    this.editableVehicleInfo.batteryType = '';
    this.reason = '';
    this.additionalReasonComment = '';
    this.modalService.open(tpl, { maxWidth: '650px', });
  }

  isOnboardedToday(vehicle: any): boolean {
    if (!vehicle || !vehicle.onboardedAt) {
      return false;
    }
    const onboardDate = new Date(vehicle.onboardedAt);
    const today = new Date();
    return (
      onboardDate.getDate() === today.getDate() &&
      onboardDate.getMonth() === today.getMonth() &&
      onboardDate.getFullYear() === today.getFullYear()
    );
  }

  updateVehicleModelListEdit(i: number, vehicleId: string, value: string, vehicleContext?: any) {
    this.editableVehicleInfo.vehicleModel = '';
    this.editableVehicleInfo.nominalUsableEnergy = '';
    this.editableVehicleInfo.netEnergyWh = 0;
    this.editableVehicleInfo.grossEnergyWh = 0;
    this.editableVehicleInfo.modelYear = '';
    this.editableVehicleInfo.productionYear = '';
    this.editableVehicleInfo.grossCapacityAh = 0;
    this.editableVehicleInfo.netCapacityAh = 0;
    this.editableVehicleInfo.batteryType = '';
    
    this.liveVehicleData[i] = { index: i, vehicleId: vehicleId, vehicleBrand: value, deviceType: vehicleContext?.deviceType }
    
    const models = this.getScopedSupportedVehiclesForIndex(i, vehicleContext)
      .filter(vehicle => vehicle.vehicleBrand === this.liveVehicleData[i].vehicleBrand)
      .map(vehicle => vehicle.vehicleModel)
      .filter((model, index, self) => model && self.indexOf(model) === index);
      
    this.liveVehicleData[i].vehicleModelList = models;
  }

  updateBatteryEnergyListEdit(i: number, value: string, vehicleContext?: any) {
    this.editableVehicleInfo.nominalUsableEnergy = '';
    this.editableVehicleInfo.netEnergyWh = 0;
    this.editableVehicleInfo.grossEnergyWh = 0;
    this.editableVehicleInfo.modelYear = '';
    this.editableVehicleInfo.productionYear = '';
    this.editableVehicleInfo.grossCapacityAh = 0;
    this.editableVehicleInfo.netCapacityAh = 0;
    this.editableVehicleInfo.batteryType = '';
    
    this.liveVehicleData[i].vehicleModel = value;
    this.updateBatteryEnergyOptions(i, vehicleContext);
  }

  updateModelYearListEdit(i: number, value: string, vehicleContext?: any) {
    this.editableVehicleInfo.modelYear = '';
    this.editableVehicleInfo.productionYear = '';
    this.editableVehicleInfo.grossCapacityAh = 0;
    this.editableVehicleInfo.netCapacityAh = 0;
    this.editableVehicleInfo.batteryType = '';
    
    this.updateModelYearOptions(i, value, vehicleContext);
  }

  updateProductionYearEdit(i: number, value: string, vehicleContext?: any) {
    this.updateProductionYearForEdit(i, value, vehicleContext);
  }

  private updateProductionYearForEdit(i: number, value: string, vehicleContext?: any): void {
    this.updateProductionYearValues(i, value, vehicleContext);
  }

  closeEditVehicle(tpl: TemplateRef<any>) {
    this.modalService.close(tpl);
  }

  editVehicleInfo(editVehicleTpl: TemplateRef<any>) {
    const body = {
      vehicleBrand: this.liveVehicleData[0].vehicleBrand,
      vehicleModel: this.liveVehicleData[0].vehicleModel,
      grossEnergyWh: this.liveVehicleData[0].grossEnergyWh,
      netEnergyWh: this.liveVehicleData[0].netEnergyWh,
      modelYear: this.liveVehicleData[0].modelYear.toString(),
      productionYear: this.liveVehicleData[0].productionYear.toString(),
      grossCapacityAh: this.liveVehicleData[0].grossCapacityAh,
      netCapacityAh: this.liveVehicleData[0].netCapacityAh,
      batteryType: this.liveVehicleData[0].batteryType,
      urlImage: this.liveVehicleData[0].urlImage,
      supported: true,
    }
    this.vehicleInfoService.updateVehicles(this.liveVehicleData[0].vehicleId, body).subscribe({
      next: (response) => {
        this.modalService.close(editVehicleTpl);
        const label = `Vehicle updated successfully.`;
        const icon = `rb-ic-alert-success`;
        this.openToast(label, icon, 'success');
      },
      error: (error) => {
        console.error('Error update vehicle:', error);
        this.modalService.close(editVehicleTpl);
      },
      complete: () => {
        this.retrieveNotIngestedVehicle();
      }
    })
  }


  sendEditVehicleInfo(editVehicleTpl: TemplateRef<any>) {
    const credentials = sessionStorage.getItem(`credentials_${environment.appId}`);
    const body = {
      vehicleBrand: this.oldVehicleData.vehicleBrand,
      vehicleModel: this.oldVehicleData.vehicleModel,
      modelYear: this.oldVehicleData.modelYear.toString(),
      productionYear: this.oldVehicleData.productionYear.toString(),
      grossEnergyWh: this.oldVehicleData.grossEnergyWh,
      netEnergyWh: this.oldVehicleData.netEnergyWh,
      grossCapacityAh: this.oldVehicleData.grossCapacityAh,
      netCapacityAh: this.oldVehicleData.netCapacityAh,
      batteryType: this.oldVehicleData.batteryType,
      vin: this.oldVehicleData.vin,
      hashedVin: this.oldVehicleData.hashedVin,
      newVehicleBrand: this.liveVehicleData[0].vehicleBrand,
      newVehicleModel: this.liveVehicleData[0].vehicleModel,
      newModelYear: this.liveVehicleData[0].modelYear.toString(),
      newProductionYear: this.liveVehicleData[0].productionYear.toString(),
      newGrossEnergyWh: this.liveVehicleData[0].grossEnergyWh,
      newNetEnergyWh: this.liveVehicleData[0].netEnergyWh,
      newGrossCapacityAh: this.liveVehicleData[0].grossCapacityAh,
      newNetCapacityAh: this.liveVehicleData[0].netCapacityAh,
      newBatteryType: this.liveVehicleData[0].batteryType,
      newUrlImage: this.liveVehicleData[0].urlImage,
      reason: this.reason,
      additionalComment: this.additionalReasonComment !== '' ? this.additionalReasonComment : 'No additional comments provided.',
      username: credentials ? JSON.parse(credentials).userName : 'Unknown user',
      database: credentials ? JSON.parse(credentials).database : 'Unknown database',
      sessionId: credentials ? JSON.parse(credentials).sessionId : 'Unknown session',
      batteryIdentifiers: this.oldVehicleData.batteryIdentifier
    }
    
    this.vehicleInfoService.requestVehicleConfigurationChange(body).subscribe({
      next: (response) => {
        this.modalService.close(editVehicleTpl);
        const label = `Vehicle configuration change request sent successfully.`;
        const icon = `rb-ic-alert-success`;
        this.openToast(label, icon, 'success');
      },
      error: (error) => {
        console.error('Error update vehicle:', error);
        this.modalService.close(editVehicleTpl);
      },
      complete: () => {
        this.retrieveNotIngestedVehicle();
      }
    })
  }

  openToast(label: string, icon: string, type: 'error' | 'success' | 'neutral') {
    this.toaster.createToast(
      label,
      icon,
      { 
        timeout: 20000,
        customProperties: { margin: '0px', type: type }
      }
    );
  }

  checkPendingRequests(credentials: any) {
    try {
      const parsedCredentials = JSON.parse(credentials);
      if(parsedCredentials.database) {
        this.vehicleInfoService.getPendingRequests(parsedCredentials.database, 'pending').pipe(takeUntil(this.unsubscribe$)).subscribe({
          next: (response) => {
            this.pendingRequests = response.data;
          },
          error: (error) => {
            console.error('Error fetching pending requests:', error);
          }
        });
      } else {
        console.error('Failed to parse credentials from localStorage:', 'Database name is missing in credentials');
      }
    } catch (error) {
      console.error('Failed to parse credentials from localStorage:', error);
    }
  }
  
  isPending(vehicle: any): boolean {
    return this.pendingRequests.some((request) => request.batteryIdentifier === vehicle.batteryIdentifier);
  }

  navigateNotSupportedVehicles(pageChange: PageChange) {
    this.lastNavigate = pageChange;
    this.pageData = new PageData({ ...this.pageData, number: pageChange.number });
    if (this.allNotSupportedVehicles && this.allNotSupportedVehicles.length > 0) {
      this.applyClientSideFilterSort();
    } else {
      this.retrieveNotSupportedVehicle();
    }
  }

  navigateNotIngestedVehicles(pageChange: PageChange) {
    this.lastNavigateIngested = pageChange;
    this.pageDataIngested = new PageData({ ...this.pageDataIngested, number: pageChange.number });
    if (this.allNotIngestedVehicles && this.allNotIngestedVehicles.length > 0) {
      this.applyClientSideFilterSortIngested();
    } else {
      this.retrieveNotIngestedVehicle();
    }
  }
}